import React, { useEffect, useState } from "react";
import {
  Col,
  Form,
  Row,
  Button,
  Spinner,
  Card,
  Container,
} from "react-bootstrap";
import { getHooksGeneratorResponse } from "../../../services/getAiResponse";
import { ToolsHeader } from "../../components/ToolsHeader";
import { isInIframe } from "../../../utils/is-in-iframe";
import { Box } from "../hooksGeneratorStyle";
import {
  validateHookTopic,
  validateTargetAudience,
} from "../../../utils/validators";

interface Hooks {
  topic: string;
  targetAudience: string;
}

interface HooksError {
  topic: string | null;
  targetAudience: string | null;
}

export default function HooksGenerators() {
  const [reloadPage, setReloadPage] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [inputData, setInputData] = useState<Hooks>({
    topic: "",
    targetAudience: "",
  });
  const [hooksResponse, setHooksResponse] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [errors, setErrors] = useState<HooksError>({
    topic: null,
    targetAudience: null,
  });

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [hooksResponse]);

  const handleReloadPage = () => {
    setHooksResponse("");
    setInputData({ topic: "", targetAudience: "" });
    setIsCopied(false);
    setReloadPage(true);
    setErrors({ topic: null, targetAudience: null });
  };

  const handleOnChange = (field: keyof Hooks, value: string) => {
    setInputData((prev) => ({ ...prev, [field]: value }));

    if (field === "topic") {
      setErrors((prev) => ({ ...prev, topic: validateHookTopic(value) }));
    } else if (field === "targetAudience") {
      setErrors((prev) => ({
        ...prev,
        targetAudience: validateTargetAudience(value),
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const topicError = validateHookTopic(inputData.topic);
    const audienceError = validateTargetAudience(inputData.targetAudience);

    setErrors({
      topic: topicError,
      targetAudience: audienceError,
    });

    if (topicError !== null || audienceError !== null) {
      return;
    }

    setShowLoader(true);

    try {
      const data = await getHooksGeneratorResponse(inputData);

      if (data) {
        setHooksResponse(data);
        setReloadPage(false);

        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: "setHeight", height }, "*");
      }
    } catch (error) {
      console.error("Error fetching hooks:", error);
    } finally {
      setShowLoader(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(hooksResponse)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1200);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  return (
    <Box>
      <Container
        className={`${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"}`}
      >
        <Row>
          <Col>
            {!isInIframe && (
              <ToolsHeader
                title="Hooks Generators"
                description="Create captivating hooks that spark curiosity and engage your target audience instantly."
              />
            )}

            <Card
              className="p-4 w-100"
              style={{
                maxWidth: "100%",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
              }}
            >
              <Form noValidate onSubmit={handleSubmit} id="hookGenerator">
                <Row>
                  <Col>
                    <Form.Label>Topic</Form.Label>
                    <Form.Group className="mb-3" controlId="controlSummary">
                      <Form.Control
                        style={{
                          fontSize: "smaller",
                          height: "100px",
                        }}
                        value={inputData.topic}
                        placeholder='Enter your content topic (e.g., "A blog post about how to boost productivity without harming employee morale through output incentives")'
                        as="textarea"
                        onChange={(e) => handleOnChange("topic", e.target.value)}
                        isInvalid={!!errors.topic}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.topic || "Topic is required."}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Label>Target Audience</Form.Label>
                    <Form.Group className="mb-3" controlId="controlAudience">
                      <Form.Control
                        as="textarea"
                        value={inputData.targetAudience}
                        placeholder='Describe your target audience (e.g., "small business owners," "tech-savvy professionals," "remote workers")'
                        onChange={(e) =>
                          handleOnChange("targetAudience", e.target.value)
                        }
                        isInvalid={!!errors.targetAudience}
                        style={{
                          fontSize: "14px",
                          minHeight: "90px",
                          lineHeight: "1.4",
                          padding: "10px",
                          resize: "vertical",
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.targetAudience || "Target Audience is required."}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col className="text-center">
                    {showLoader ? (
                      <Spinner animation="border" role="status" className="m-2">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      <Button
                        className="m-2"
                        style={{ backgroundColor: "#8cc540" }}
                        type="submit"
                      >
                        Submit
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>

              {!reloadPage && (
                <Row className="mt-4">
                  <Card
                    style={{
                      boxShadow:
                        "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
                    }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-end mb-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={handleReloadPage}
                        >
                          ↻ Reload
                        </Button>
                        <Button
                          variant={isCopied ? "success" : "outline-secondary"}
                          size="sm"
                          onClick={handleCopy}
                        >
                          {isCopied ? "✓ Copied" : "📋 Copy"}
                        </Button>
                      </div>

                      <pre style={{ whiteSpace: "pre-wrap" }}>
                        {hooksResponse}
                      </pre>
                    </Card.Body>
                  </Card>
                </Row>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </Box>
  );
}